/**
 * Utilitários para validação padronizada
 * REGRA 13: Validações inconsistentes
 */

import { validarCPF, validarCNPJ, formatarCPF, formatarCNPJ, formatarTelefone, formatarCEP } from './formatadores.js';

/**
 * Tipos de validação disponíveis
 */
export const VALIDATION_TYPES = {
  REQUIRED: 'required',
  EMAIL: 'email',
  CPF: 'cpf',
  CNPJ: 'cnpj',
  TELEFONE: 'telefone',
  CEP: 'cep',
  MIN_LENGTH: 'minLength',
  MAX_LENGTH: 'maxLength',
  NUMERIC: 'numeric',
  POSITIVE: 'positive',
  DATE: 'date',
  TIME: 'time',
  CUSTOM: 'custom'
};

/**
 * Valida um campo específico
 * @param {string} value - Valor a ser validado
 * @param {string} type - Tipo de validação
 * @param {Object} options - Opções adicionais
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateField = (value, type, options = {}) => {
  // Se o campo é obrigatório e está vazio
  if (type === VALIDATION_TYPES.REQUIRED || options.required) {
    if (!value || value.toString().trim() === '') {
      return {
        isValid: false,
        message: options.message || 'Este campo é obrigatório.'
      };
    }
  }

  // Se o valor está vazio e não é obrigatório, é válido
  if (!value || value.toString().trim() === '') {
    return { isValid: true, message: '' };
  }

  const stringValue = value.toString().trim();

  switch (type) {
    case VALIDATION_TYPES.EMAIL:
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        return {
          isValid: false,
          message: options.message || 'E-mail inválido.'
        };
      }
      break;

    case VALIDATION_TYPES.CPF:
      if (!validarCPF(stringValue)) {
        return {
          isValid: false,
          message: options.message || 'CPF inválido.'
        };
      }
      break;

    case VALIDATION_TYPES.CNPJ:
      if (!validarCNPJ(stringValue)) {
        return {
          isValid: false,
          message: options.message || 'CNPJ inválido.'
        };
      }
      break;

    case VALIDATION_TYPES.TELEFONE:
      const telefoneRegex = /^\(\d{2}\) \d{4,5}-\d{4}$/;
      if (!telefoneRegex.test(formatarTelefone(stringValue))) {
        return {
          isValid: false,
          message: options.message || 'Telefone inválido.'
        };
      }
      break;

    case VALIDATION_TYPES.CEP:
      const cepRegex = /^\d{5}-\d{3}$/;
      if (!cepRegex.test(formatarCEP(stringValue))) {
        return {
          isValid: false,
          message: options.message || 'CEP inválido.'
        };
      }
      break;

    case VALIDATION_TYPES.MIN_LENGTH:
      if (stringValue.length < options.min) {
        return {
          isValid: false,
          message: options.message || `Mínimo de ${options.min} caracteres.`
        };
      }
      break;

    case VALIDATION_TYPES.MAX_LENGTH:
      if (stringValue.length > options.max) {
        return {
          isValid: false,
          message: options.message || `Máximo de ${options.max} caracteres.`
        };
      }
      break;

    case VALIDATION_TYPES.NUMERIC:
      if (isNaN(Number(stringValue))) {
        return {
          isValid: false,
          message: options.message || 'Apenas números são permitidos.'
        };
      }
      break;

    case VALIDATION_TYPES.POSITIVE:
      const numValue = Number(stringValue);
      if (isNaN(numValue) || numValue <= 0) {
        return {
          isValid: false,
          message: options.message || 'Valor deve ser positivo.'
        };
      }
      break;

    case VALIDATION_TYPES.DATE:
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!dateRegex.test(stringValue)) {
        return {
          isValid: false,
          message: options.message || 'Data inválida (DD/MM/AAAA).'
        };
      }
      break;

    case VALIDATION_TYPES.TIME:
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(stringValue)) {
        return {
          isValid: false,
          message: options.message || 'Hora inválida (HH:MM).'
        };
      }
      break;

    case VALIDATION_TYPES.CUSTOM:
      if (options.validator && typeof options.validator === 'function') {
        const customResult = options.validator(stringValue);
        if (!customResult.isValid) {
          return customResult;
        }
      }
      break;
  }

  return { isValid: true, message: '' };
};

/**
 * Valida um objeto de formulário completo
 * @param {Object} formData - Dados do formulário
 * @param {Object} validationRules - Regras de validação
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export const validateForm = (formData, validationRules) => {
  const errors = {};
  let isValid = true;

  Object.keys(validationRules).forEach(fieldName => {
    const fieldRules = validationRules[fieldName];
    const fieldValue = formData[fieldName];

    // Se é um array de regras
    if (Array.isArray(fieldRules)) {
      for (const rule of fieldRules) {
        const result = validateField(fieldValue, rule.type, rule.options);
        if (!result.isValid) {
          errors[fieldName] = result.message;
          isValid = false;
          break;
        }
      }
    } else {
      // Se é uma única regra
      const result = validateField(fieldValue, fieldRules.type, fieldRules.options);
      if (!result.isValid) {
        errors[fieldName] = result.message;
        isValid = false;
      }
    }
  });

  return { isValid, errors };
};

/**
 * Validação específica para horários de atendimento
 * @param {Object} formData - Dados do formulário
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export const validateHorarioAtendimento = (formData) => {
  const validationRules = {
    nome: [
      { type: VALIDATION_TYPES.REQUIRED },
      { type: VALIDATION_TYPES.MIN_LENGTH, options: { min: 3 } },
      { type: VALIDATION_TYPES.MAX_LENGTH, options: { max: 100 } }
    ],
    diaInicio: [
      { type: VALIDATION_TYPES.REQUIRED },
      { type: VALIDATION_TYPES.DATE }
    ],
    diaFim: [
      { type: VALIDATION_TYPES.REQUIRED },
      { type: VALIDATION_TYPES.DATE }
    ],
    horaInicio: [
      { type: VALIDATION_TYPES.REQUIRED },
      { type: VALIDATION_TYPES.TIME }
    ],
    horaFim: [
      { type: VALIDATION_TYPES.REQUIRED },
      { type: VALIDATION_TYPES.TIME }
    ],
    duracao_minuto: [
      { type: VALIDATION_TYPES.REQUIRED },
      { type: VALIDATION_TYPES.NUMERIC },
      { type: VALIDATION_TYPES.POSITIVE }
    ],
    local: [
      { type: VALIDATION_TYPES.REQUIRED }
    ],
    servico: [
      { type: VALIDATION_TYPES.REQUIRED }
    ],
    colaborador: [
      { type: VALIDATION_TYPES.REQUIRED }
    ],
    diasSelecionados: [
      { 
        type: VALIDATION_TYPES.CUSTOM, 
        options: { 
          validator: (value) => {
            if (!Array.isArray(value) || value.length === 0) {
              return { isValid: false, message: 'Selecione pelo menos um dia da semana.' };
            }
            return { isValid: true, message: '' };
          }
        }
      }
    ]
  };

  return validateForm(formData, validationRules);
};

/**
 * Validação específica para datas (verifica se data fim é posterior à data início)
 * @param {string} dataInicio - Data de início
 * @param {string} dataFim - Data de fim
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateDateRange = (dataInicio, dataFim) => {
  if (!dataInicio || !dataFim) {
    return { isValid: true, message: '' };
  }

  try {
    const [diaInicio, mesInicio, anoInicio] = dataInicio.split('/');
    const [diaFim, mesFim, anoFim] = dataFim.split('/');
    
    const dateInicio = new Date(anoInicio, mesInicio - 1, diaInicio);
    const dateFim = new Date(anoFim, mesFim - 1, diaFim);
    
    if (dateFim <= dateInicio) {
      return {
        isValid: false,
        message: 'A data de fim deve ser posterior à data de início.'
      };
    }
    
    return { isValid: true, message: '' };
  } catch (error) {
    return {
      isValid: false,
      message: 'Erro ao validar datas.'
    };
  }
};

/**
 * Validação específica para horários (verifica se hora fim é posterior à hora início)
 * @param {string} horaInicio - Hora de início
 * @param {string} horaFim - Hora de fim
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateTimeRange = (horaInicio, horaFim) => {
  if (!horaInicio || !horaFim) {
    return { isValid: true, message: '' };
  }

  try {
    const [horaInicioNum, minInicioNum] = horaInicio.split(':').map(Number);
    const [horaFimNum, minFimNum] = horaFim.split(':').map(Number);
    
    const minutosInicio = horaInicioNum * 60 + minInicioNum;
    const minutosFim = horaFimNum * 60 + minFimNum;
    
    if (minutosFim <= minutosInicio) {
      return {
        isValid: false,
        message: 'A hora de fim deve ser posterior à hora de início.'
      };
    }
    
    return { isValid: true, message: '' };
  } catch (error) {
    return {
      isValid: false,
      message: 'Erro ao validar horários.'
    };
  }
};


